import { useCallback } from 'react';
import { DOCUMENT_MAX_LENGTHS, DOCUMENT_TYPES } from '../config/dteConfig';

/**
 * Hook personalizado para validaciones de documentos DTE
 * Proporciona funciones de validación para diferentes tipos de documentos
 */
export const useDTEValidation = () => {
  // ============================================
  // VALIDADORES DE DOCUMENTOS DE IDENTIFICACIÓN
  // ============================================

  /**
   * Valida un DUI (Documento Único de Identidad) de El Salvador
   * Formato: ########-# (8 dígitos + dígito verificador)
   */
  const validarDUI = useCallback((dui) => {
    if (!/^\d{8}-\d$/.test(dui)) return false;

    const [num, dv] = dui.split("-");
    let suma = 0;

    for (let i = 0; i < 8; i++) {
      suma += parseInt(num[i]) * (9 - i);
    }

    const verificador = (10 - (suma % 10)) % 10;

    return verificador === parseInt(dv);
  }, []);

  /**
   * Valida un NIT (Número de Identificación Tributaria) de El Salvador
   * Formato: ####-######-###-# (4-6-3-1 = 14 dígitos)
   */
  const validarNIT = useCallback((nit) => {
    // MH admite identificadores normalizados de 9 o 14 dígitos; la UI puede
    // capturar el NIT histórico con guiones y el builder lo normaliza.
    if (/^\d{9}$/.test(nit)) return true;
    if (!/^\d{4}-\d{6}-\d{3}-\d$/.test(nit)) return false;

    const clean = nit.replace(/-/g, "");
    
    // Validar que tenga exactamente 14 dígitos
    if (clean.length !== 14) return false;
    
    // Los primeros 4 dígitos son el código de registro (debe ser 0000-9999)
    const codigoRegistro = parseInt(clean.substring(0, 4));
    if (isNaN(codigoRegistro) || codigoRegistro < 0 || codigoRegistro > 9999) return false;
    
    // Posiciones 4-9: fecha de nacimiento (DDMMAA)
    const fecha = clean.substring(4, 10);
    const dia = parseInt(fecha.substring(0, 2));
    const mes = parseInt(fecha.substring(2, 4));
    const anio = parseInt(fecha.substring(4, 6));
    
    if (isNaN(dia) || dia < 1 || dia > 31) return false;
    if (isNaN(mes) || mes < 1 || mes > 12) return false;
    if (isNaN(anio) || anio < 0 || anio > 99) return false;
    
    // Por ahora, no validamos el dígito verificador
    // Solo validamos el formato y la fecha
    return true;
  }, []);

  /**
   * Valida un NRC (Número de Registro de Contribuyente)
   * Formato: 1-8 dígitos
   */
  const validarNRC = useCallback((nrc) => {
    return /^\d{1,8}$/.test(nrc);
  }, []);

  /**
   * Valida un Carnet de Residente
   * Formato: Letra seguida de 6-7 dígitos (ej: A1234567)
   */
  const validarCarnetResidente = useCallback((cr) => {
    const clean = cr.replace(/[\s-]/g, "").toUpperCase();
    return /^[A-Z]\d{6,7}$/.test(clean);
  }, []);

  /**
   * Valida un Pasaporte
   * Formato: Letra seguida de 6-8 números (ej: A1234567)
   */
  const validarPasaporte = useCallback((pass) => {
    const clean = pass.replace(/\s/g, "").toUpperCase();
    return /^[A-Z][0-9]{6,8}$/.test(clean);
  }, []);

  /**
   * Valida documentos de tipo "Otro"
   * Longitud: 3-20 caracteres
   */
  const validarOtro = useCallback((numDoc) => {
    const cleanNum = numDoc.replace(/-/g, '');
    return cleanNum.length >= 3 && cleanNum.length <= 20;
  }, []);

  /**
   * Valida correo electrónico
   */
  const validarEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, []);

  /**
   * Valida número de WhatsApp
   */
  const validarWhatsApp = useCallback((whatsapp) => {
    if (!whatsapp) return true; // Whatsapp es opcional
    return whatsapp.length >= 8;
  }, []);

  // ============================================
  // VALIDACIONES PARA ANULACIONES
  // ============================================

  /**
   * Valida el formato del número de control
   * Formato: DTE-01/02/.../15-XXXXXXXX-XXXXXXXXXXXXXXX
   */
  const validarNumeroControl = useCallback((numero) => {
    if (!numero) return false;
    // DTE-[01-15]-[8 dígitos]-[15 dígitos]
    return /^(?:DTE-0[1-9]|DTE-1[0-5])-[A-Z0-9]{8}-[0-9]{15}$/i.test(numero);
  }, []);

  /**
   * Valida el formato del código de generación (UUID)
   */
  const validarCodigoGeneracion = useCallback((codigo) => {
    if (!codigo) return false;
    const uuidPattern = /^[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12}$/i;
    return uuidPattern.test(codigo);
  }, []);

  // ============================================
  // MAPA DE VALIDADORES
  // ============================================

  const validators = {
    '36': validarNIT,
    '13': validarDUI,
    '02': validarCarnetResidente,
    '03': validarPasaporte,
    '37': validarOtro,
  };

  // ============================================
  // FUNCIONES PÚBLICAS
  // ============================================

  /**
   * Valida un número de documento según su tipo
   * @param {string} tipoDoc - Tipo de documento (36, 13, 02, 03, 37)
   * @param {string} numDoc - Número de documento a validar
   * @returns {boolean} - true si es válido
   */
  const validateDocument = useCallback((tipoDoc, numDoc) => {
    if (!tipoDoc || !numDoc) return false;
    
    const validator = validators[tipoDoc];
    if (!validator) return true; // Si no hay validador específico, permitir
    
    return validator(numDoc);
  }, [validators]);

  /**
   * Obtiene el mensaje de error para un tipo de documento inválido
   * @param {string} tipoDoc - Tipo de documento
   * @returns {string} - Mensaje de error
   */
  const getDocumentErrorMessage = useCallback((tipoDoc) => {
    const messages = {
      '36': 'NIT inválido. Formato: ####-######-###-#',
      '13': 'DUI inválido. Formato: ########-#',
      '02': 'Carnet Residente inválido. Formato: Letra seguida de 6-7 dígitos (ej: A1234567)',
      '03': 'Pasaporte inválido. Formato: Letra seguida de 6-8 dígitos (ej: A1234567)',
      '37': 'Documento debe tener entre 3 y 20 caracteres',
    };
    
    return messages[tipoDoc] || 'Documento inválido';
  }, []);

  /**
   * Valida el nombre o razón social del receptor
   */
  const validateReceptorName = useCallback((nombre) => {
    if (!nombre) return 'Nombre o razón social es requerido';
    if (nombre.length < 5) return 'Nombre debe tener al menos 5 caracteres';
    return null;
  }, []);

  /**
   * Valida el NRC del receptor
   */
  const validateReceptorNRC = useCallback((nrc) => {
    if (!nrc) return 'NRC es requerido';
    if (!validarNRC(nrc)) return 'NRC inválido. Solo números, máximo 8 dígitos';
    return null;
  }, [validarNRC]);

  /**
   * Valida el contacto (correo o WhatsApp) del receptor
   */
  const validateReceptorContact = useCallback((receptor) => {
    const hasEmail = receptor?.correoNotificacion && validarEmail(receptor.correoNotificacion);
    const hasWhatsApp = receptor?.whatsapp && validarWhatsApp(receptor.whatsapp);
    
    if (!hasEmail && !hasWhatsApp) {
      return 'Correo o WhatsApp para recibir el DTE es requerido';
    }
    return null;
  }, [validarEmail, validarWhatsApp]);

  /**
   * Calcula el umbral de búsqueda (50% de caracteres)
   * @param {string} tipoDoc - Tipo de documento
   * @returns {number} - Número de caracteres para iniciar búsqueda
   */
  const getSearchThreshold = useCallback((tipoDoc) => {
    const maxLength = DOCUMENT_MAX_LENGTHS[tipoDoc];
    if (!maxLength) return 0;
    return Math.ceil(maxLength * 0.5);
  }, []);

  /**
   * Limpia el número de documento según el tipo
   * @param {string} numDoc - Número de documento
   * @returns {string} - Número limpio sin máscara
   */
  const cleanDocumentNumber = useCallback((numDoc) => {
    return numDoc.replace(/[^0-9A-Za-z]/g, '');
  }, []);

  return {
    // Validadores individuales
    validarDUI,
    validarNIT,
    validarNRC,
    validarCarnetResidente,
    validarPasaporte,
    validarOtro,
    validarEmail,
    validarWhatsApp,
    // Validadores de anulación
    validarNumeroControl,
    validarCodigoGeneracion,
    
    // Funciones compuestas
    validateDocument,
    getDocumentErrorMessage,
    validateReceptorName,
    validateReceptorNRC,
    validateReceptorContact,
    getSearchThreshold,
    cleanDocumentNumber,
    
    // Mapa de validadores
    validators,
  };
};

export default useDTEValidation;
